import { cargoOperationType, eventType } from './enums.js';

export const getActivities = (events, eventsFilteredOnEospSops, startTimeLocalTime, stopTimeLocalTime, portCallId, portCalls) => {
    const result = {
        loadingTimes: getLoadingTime(startTimeLocalTime, stopTimeLocalTime, events),
        unloadingTimes: getUnloadingTime(startTimeLocalTime, stopTimeLocalTime, events),
        idleTime: getIdleTime(startTimeLocalTime, stopTimeLocalTime, events, eventsFilteredOnEospSops),
        shiftingMooringTime: getShiftingMooringTime(startTimeLocalTime, stopTimeLocalTime, events, eventsFilteredOnEospSops),
        tankCleaningTime: getTankCleaningTime(startTimeLocalTime, stopTimeLocalTime, events),
        dryDockingTime: getDryDockingTime(startTimeLocalTime, stopTimeLocalTime, events),
        anchorageTime: getAnchorageTime(startTimeLocalTime, stopTimeLocalTime, events),
        steamingForTankCleaningTime: getSteamingForTankCleaningTime(startTimeLocalTime, stopTimeLocalTime, events),
        canalTransitTime: getCanalTransitTime(startTimeLocalTime, stopTimeLocalTime, eventsFilteredOnEospSops, portCallId, portCalls),
        operationalTargetSpeedTime: getOperationalTargetSpeedTime(startTimeLocalTime, stopTimeLocalTime, events),
        operationalTargetConsumptionTime: getOperationalTargetConsumptionTime(startTimeLocalTime, stopTimeLocalTime, events),
        operationalNonTargetTime: getOperationalNonTargetTime(startTimeLocalTime, stopTimeLocalTime, events),
        operationalAwaitingOrdersOrDriftingTime: getOperationalAwaitingOrdersOrDriftingTime(startTimeLocalTime, stopTimeLocalTime, events),
        operationalTankPreparationTime: getOperationalTankPreparationTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalEcaZoneTime: getNavigationalEcaZoneTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalCasualtyAndSarTime: getNavigationalCasualtyAndSarTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalCasualtyAndSarDrillTime: getNavigationalCasualtyAndSarDrillTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalHighRiskAreaTime: getNavigationalHighRiskAreaTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalPilotManeuveringTime: getNavigationalPilotManeuveringTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalRiverPortManeuveringTime: getNavigationalRiverPortManeuveringTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalSafetyManeuveringTime: getNavigationalSafetyManeuveringTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalStormShelteringTime: getNavigationalStormShelteringTime(startTimeLocalTime, stopTimeLocalTime, events),
        navigationalTrafficManeuveringTime: getNavigationalTrafficManeuveringTime(startTimeLocalTime, stopTimeLocalTime, events),
        technicalAuxiliarySystemFailureTime: getTechnicalAuxiliarySystemFailureTime(startTimeLocalTime, stopTimeLocalTime, events),
        technicalFuelQualityIssueTime: getTechnicalFuelQualityIssueTime(startTimeLocalTime, stopTimeLocalTime, events),
        technicalOtherTechnicalIssueTime: getTechnicalOtherTechnicalIssueTime(startTimeLocalTime, stopTimeLocalTime, events),
        technicalPropulsionSystemFailureTime: getTechnicalPropulsionSystemFailureTime(startTimeLocalTime, stopTimeLocalTime, events),
        timeZoneChangeHours: getTimeZoneChangeHours(startTimeLocalTime, stopTimeLocalTime, events),
        portDescription: getAolListItemPortDescription(portCallId, portCalls),
    };
    return result;
};

const getAolListItemPortDescription = (portCallId, portCalls) => {
    const portCall = portCalls.find((o) => o.imosPortCallId === portCallId);
    return portCall?.port?.port?.portCluster?.portClusterName ?? portCall?.port?.portName ?? '???';
};

const getTimeZoneChangeHours = (startTimeLocalTime, stopTimeLocalTime, events) => {
    // For IDL-spanning items, local start >= stop. Use UTC for filtering when available.
    const useUtcFilter = startTimeLocalTime >= stopTimeLocalTime;
    return events
        .filter(
            (o) =>
                (o.eventTypeId === eventType.operationalAdjustHoursAfterTimeZoneChange || o.eventTypeId === eventType.operationalIdlCrossing) &&
                (useUtcFilter && o.eventDate.actualDateTimeUtc
                    ? true // Include all TZ/IDL events when local range is inverted (they'll be in the item's UTC range)
                    : o.eventDate.actualDateTimeLocal > startTimeLocalTime &&
                      o.eventDate.actualDateTimeLocal <= stopTimeLocalTime)
        )
        .reduce((sum, o) => sum + getTimeZoneHoursFromEvent(o), 0);
};

const getTimeZoneHoursFromEvent = (ev) => {
    const eventTypeInputReg = ev.eventTypeInputRegList.find((x) => x.inputDataTypeId === 6);

    if (eventTypeInputReg) {
        const itemReg = eventTypeInputReg.dropdownGroupReg.dropdownItemRegList[0];

        if (itemReg) {
            let itemValue = itemReg.dropdownItemValue.replace('h', '');
            return parseFloat(itemValue);
        }
    }

    return 0;
};

const getLoadingTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const cargoLoadingEvents = events.filter(isCargoLoadingEvent);
    const fromTos = getFromTos(
        cargoLoadingEvents,
        [eventType.pumpingCommence, eventType.pumpingResumed],
        [eventType.pumpingComplete, eventType.pumpingStopped],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getUnloadingTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const cargoUnloadingEvents = events.filter(isCargoUnloadingEvent);
    const fromTos = getFromTos(
        cargoUnloadingEvents,
        [eventType.pumpingCommence, eventType.pumpingResumed],
        [eventType.pumpingComplete, eventType.pumpingStopped],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getIdleTime = (startTimeLocalTime, stopTimeLocalTime, events, eventsFilteredOnEospSops) => {
    const cargoLoadingEvents = events.filter(isCargoLoadingEvent);
    const cargoUnloadingEvents = events.filter(isCargoUnloadingEvent);
    const portTimeSpan = getPortTime(startTimeLocalTime, stopTimeLocalTime, eventsFilteredOnEospSops);

    const loadingTimeSpan = getOverlappingTimeSpan(
        getFromTos(
            cargoLoadingEvents,
            [eventType.pumpingCommence, eventType.pumpingResumed],
            [eventType.pumpingComplete, eventType.pumpingStopped],
            startTimeLocalTime,
            stopTimeLocalTime
        )
    );

    const unloadingTimeSpan = getOverlappingTimeSpan(
        getFromTos(
            cargoUnloadingEvents,
            [eventType.pumpingCommence, eventType.pumpingResumed],
            [eventType.pumpingComplete, eventType.pumpingStopped],
            startTimeLocalTime,
            stopTimeLocalTime
        )
    );

    return formatTimeSpanInMilliseconds(Math.max(0, portTimeSpan - loadingTimeSpan - unloadingTimeSpan));
};

const getShiftingMooringTime = (startTimeLocalTime, stopTimeLocalTime, events, eventsFilteredOnEospSops) => {
    const portTimeSpan = getPortTime(startTimeLocalTime, stopTimeLocalTime, eventsFilteredOnEospSops);

    const berthAndAnchoringTimeSpan = getOverlappingTimeSpan(
        getFromTos(
            events,
            [
                eventType.allFast,
                eventType.wellAnchoredCargoOps,
                eventType.wellAnchoredNoCargo,
                eventType.wellAnchored,
                eventType.dryDockingCommenced,
                eventType.dryDockingBerthCommenced,
                eventType.bunkerBargeAlongsideAtBerth,
            ],
            [
                eventType.allClear,
                eventType.anchorAweighCargoOps,
                eventType.anchorAweighNoCargo,
                eventType.anchorAweigh,
                eventType.dryDockingCompleted,
                eventType.dryDockingBerthCompleted,
                eventType.bunkerBargeCastedOffAtBerth,
            ],
            startTimeLocalTime,
            stopTimeLocalTime
        )
    );

    return formatTimeSpanInMilliseconds(portTimeSpan - berthAndAnchoringTimeSpan);
};

const getTankCleaningTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(events, [eventType.tankCleaningCommenced], [eventType.tankCleaningCompleted], startTimeLocalTime, stopTimeLocalTime);
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getSteamingForTankCleaningTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.steamingForTankCleaningCommenced],
        [eventType.steamingForTankCleaningCommenced],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getAnchorageTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(events, [eventType.wellAnchored], [eventType.anchorAweigh], startTimeLocalTime, stopTimeLocalTime);
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getDryDockingTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.dryDockingCommenced, eventType.dryDockingBerthCommenced],
        [eventType.dryDockingCompleted, eventType.dryDockingBerthCompleted],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getOperationalTargetSpeedTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.operationalTargetSpeed, eventType.operationalChangeToTargetSpeed],
        [eventType.operationalChangeToTargetConsumption, eventType.operationalChangeToNonTargetVoyage, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getOperationalTargetConsumptionTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.operationalTargetConsumption, eventType.operationalChangeToTargetConsumption],
        [eventType.operationalChangeToTargetSpeed, eventType.operationalChangeToNonTargetVoyage, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getOperationalNonTargetTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.operationalNonTargetVoyage, eventType.operationalChangeToNonTargetVoyage],
        [eventType.operationalChangeToTargetConsumption, eventType.operationalChangeToTargetSpeed, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getOperationalAwaitingOrdersOrDriftingTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.operationalStartAwaitingOrdersOrDrifting],
        [eventType.operationalStopAwaitingOrdersOrDrifting, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getOperationalTankPreparationTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.operationalStartTankPreparation],
        [eventType.operationalStopTankPreparation, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalEcaZoneTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalEnteringEcaZone],
        [eventType.navigationalLeavingEcaZone, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalCasualtyAndSarTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartCasualtyAndSar],
        [eventType.navigationalStopCasualtyAndSar, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalCasualtyAndSarDrillTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartCasualtyAndSarDrill],
        [eventType.navigationalStopCasualtyAndSarDrill, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalHighRiskAreaTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartHighRiskArea],
        [eventType.navigationalStopHighRiskArea, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalPilotManeuveringTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartPilotManeuvering],
        [eventType.navigationalStopPilotManeuvering, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalRiverPortManeuveringTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartRiverPortManeuvering],
        [eventType.navigationalStopRiverPortManeuvering, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalSafetyManeuveringTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartSafetyManeuvering],
        [eventType.navigationalStopSafetyManeuvering, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalStormShelteringTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartStormSheltering],
        [eventType.navigationalStopStormSheltering, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getNavigationalTrafficManeuveringTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.navigationalStartTrafficManeuvering],
        [eventType.navigationalStopTrafficManeuvering, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getTechnicalAuxiliarySystemFailureTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.technicalStartAuxiliarySystemFailure],
        [eventType.technicalStopAuxiliarySystemFailure, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getTechnicalFuelQualityIssueTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.technicalStartFuelQualityIssue],
        [eventType.technicalStopFuelQualityIssue, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getTechnicalOtherTechnicalIssueTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.technicalStartOtherTechnicalIssue],
        // Use the correct stop event (was incorrectly using start event as stop)
        [eventType.technicalStopOtherTechnicalIssue, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getTechnicalPropulsionSystemFailureTime = (startTimeLocalTime, stopTimeLocalTime, events) => {
    const fromTos = getFromTos(
        events,
        [eventType.technicalStartPropulsionSystemFailure],
        [eventType.technicalStopPropulsionSystemFailure, eventType.eosp],
        startTimeLocalTime,
        stopTimeLocalTime
    );
    return formatTimeSpanInMilliseconds(getOverlappingTimeSpan(fromTos));
};

const getPortTime = (startTimeLocalTime, stopTimeLocalTime, eospSospAllFastAllClearEvents) => {
    return getOverlappingTimeSpan(
        getFromTos(eospSospAllFastAllClearEvents, [eventType.eosp], [eventType.sosp], startTimeLocalTime, stopTimeLocalTime)
    );
};

const getCanalTransitTime = (startTimeLocalTime, stopTimeLocalTime, eospSospAllFastAllClearEvents, imosPortCallId, portCalls) =>
    formatTimeSpanInMilliseconds(
        isCanalTransit(imosPortCallId, portCalls) ? getPortTime(startTimeLocalTime, stopTimeLocalTime, eospSospAllFastAllClearEvents) : 0
    );

const isCanalTransit = (imosPortCallId, portCalls) => {
    const portCall = portCalls.find((o) => o.imosPortFunction?.toUpperCase() === 'I' && o.imosPortCallId === imosPortCallId);
    return portCall !== null && portCall !== undefined;
};

const isCargoLoadingEvent = (ev) => isCargoLoadingType(ev, cargoOperationType.loading);
const isCargoUnloadingEvent = (ev) => isCargoLoadingType(ev, cargoOperationType.unloading);

const isCargoLoadingType = (ev, cargoOpType) => {
    // Must not use truthy checks; 0 is a valid operationType (loading)
    if (ev && ev.berthCallId != null && ev.cargoOperationId != null && ev.operationType != null) {
        return ev.operationType === cargoOpType;
    }
    return false;
};

const getFromTos = (events, startEventTypeIds, stopEventTypeIds, from, to) => {
    const startEvents = getActualEventDatesForEventTypes(events, startEventTypeIds);
    let stopEvents = getActualEventDatesForEventTypes(events, stopEventTypeIds);

    const fromTos = [];

    for (const se of startEvents) {
        // Filter stopEvents to only include dates >= se, sort descending
        const filteredStops = stopEvents.filter((o) => o >= se).sort((a, b) => b - a);
        stopEvents = [...filteredStops]; // simulate Stack with array

        const toe = stopEvents.length > 0 ? stopEvents.pop() : new Date(8640000000000000); // DateTime.MaxValue

        const fromEventTime = transformFromEventTime(se, from);
        const toEventTime = transformToEventTime(toe, to);

        if (from <= fromEventTime && fromEventTime < to && from < toEventTime && toEventTime <= to) {
            fromTos.push({ from: fromEventTime, to: toEventTime });
        }
    }

    return fromTos;
};

const transformFromEventTime = (actualEventDateUtc, from) => {
    return actualEventDateUtc < from ? from : actualEventDateUtc;
};

const transformToEventTime = (actualEventDateUtc, to) => {
    return actualEventDateUtc > to ? to : actualEventDateUtc;
};

const getActualEventDatesForEventTypes = (events, eventTypeIds) => {
    const arr = [];
    for (const ev of events) {
        if (eventTypeIds.includes(ev.eventTypeId) && ev.eventDate && ev.eventDate.actualDateTimeLocal != null) {
            let d = ev.eventDate.actualDateTimeLocal;
            if (!(d instanceof Date)) d = new Date(d);
            if (!isNaN(d)) arr.push(d);
        }
    }
    // Ascending order to mirror C# enumeration over stack (which ends up ascending)
    arr.sort((a, b) => a - b);
    return arr;
};

const getOverlappingTimeSpan = (fromTos) => {
    if (!fromTos || fromTos.length === 0) return 0;
    // Merge intervals (union of covered time)
    const sorted = [...fromTos].sort((a, b) => a.from - b.from);
    let currentStart = sorted[0].from;
    let currentEnd = sorted[0].to;
    let total = 0;
    for (let i = 1; i < sorted.length; i++) {
        const seg = sorted[i];
        if (seg.from > currentEnd) {
            // disjoint
            total += currentEnd - currentStart;
            currentStart = seg.from;
            currentEnd = seg.to;
        } else if (seg.to > currentEnd) {
            currentEnd = seg.to;
        }
    }
    total += currentEnd - currentStart;
    return total;
};

const formatTimeSpanInMilliseconds = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Pad single digits with leading zeros
    const paddedHours = String(hours).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(seconds).padStart(2, '0');

    // Construct the formatted string
    if (days > 0) {
        return `${days}.${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
        return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    }
};
